import {
    Button,
    Overlay,
    Paper,
    Typography,
    resetButtonStyle
} from "matsuri-ui"
import { css } from "@emotion/react"
import { useMemo, useRef, useState } from "react"
import CloseIcon from "@mui/icons-material/Close"
import React from "react"
import { theme } from "../theme"

export interface SelectModalOption {
    label: string
    value: string
}

// 画面全体を覆い尽くすコンポーネントなので、個別に使うよりはページ全体で1つのコンポーネントとして宣言してそれを切り替えて使うことを想定
export const SelectModal = ({
    open,
    options,
    onClose,
    onChange
}: {
    open?: boolean
    options?: SelectModalOption[]
    onClose?: () => void
    onChange?: (value: string) => void
}) => {
    return (
        <Overlay
            position="bottom"
            fullScreen
            open={open ?? false}
            css={css`
                padding: 0;
            `}
        >
            <div
                css={css`
                    position: relative;
                    display: grid;
                    overflow: auto;
                `}
            >
                <div
                    css={css`
                        width: 100%;
                        height: calc(100vh - 350px);
                        height: calc(100dvh - 350px);
                    `}
                />
                <Paper
                    css={[
                        css`
                            border-radius: 3px 3px 0 0;

                            & > *:not(:last-of-type) {
                                border-bottom: 1px solid
                                    ${
                                        theme.palette.intentions.cleanerGray
                                            .light
                                    };
                            }
                        `
                    ]}
                >
                    <div
                        css={css`
                            display: flex;
                            justify-content: flex-end;
                        `}
                    >
                        <Button
                            type="button"
                            icon={
                                <CloseIcon
                                    css={css`
                                        font-size: 32px;
                                    `}
                                />
                            }
                            css={css`
                                margin: 4px 8px;
                            `}
                            onClick={onClose}
                        />
                    </div>
                    {options?.map(option => (
                        <div key={option.value}>
                            <button
                                css={[
                                    resetButtonStyle,
                                    css`
                                        display: flex;
                                        justify-content: flex-start;
                                        width: 100%;

                                        &:active {
                                            background-color: ${
                                                theme.palette.intentions
                                                    .cleanerGray.light
                                            };
                                        }
                                    `
                                ]}
                                onClick={() => onChange?.(option.value)}
                            >
                                <Typography
                                    variant="button"
                                    as="span"
                                    css={css`
                                        padding: 12px 16px;
                                        font-weight: 400;
                                    `}
                                >
                                    {option.label}
                                </Typography>
                            </button>
                        </div>
                    ))}
                </Paper>
            </div>
        </Overlay>
    )
}

// 用途として、画面に複数のfieldがありクリックしたらそれぞれSelectModalを開くみたいなケースに使うことを想定
// そのため、openするごとにoptionsを変えられた方が良いだろうということでこのような感じにしているがパフォーマンスが犠牲になってそうなのは少し気になる
// また、選択が完了した際に答えが返ってきてくれると嬉しいため(というか、そうでないとどうやってopenした側が選択された値を受け取るのかという問題が出てしまう)、Promiseで返している
// かなり特殊なインターフェイスなので利用の際は注意が必要
interface SelectModalContextState {
    open: (options: SelectModalOption[]) => Promise<string | undefined>
}

const SelectModalContext = React.createContext<
    SelectModalContextState | undefined
>(undefined)

export const useSelectModal = () => {
    const context = React.useContext(SelectModalContext)
    if (!context) {
        throw new Error("useSelectModal must be used within a SelectModal")
    }

    return context
}

export const SelectModalProvider = ({
    children
}: {
    children: React.ReactNode
}) => {
    const [open, setOpen] = useState(false)
    const [options, setOptions] = useState<SelectModalOption[]>([])
    const resolver = useRef<((value: string | undefined) => void) | null>(null)

    const value = useMemo(
        () => ({
            open: (options: SelectModalOption[]) => {
                setOpen(true)
                setOptions(options)

                return new Promise<string | undefined>(resolve => {
                    resolver.current = resolve
                })
            }
        }),
        []
    )

    return (
        <SelectModalContext.Provider value={value}>
            {children}

            <SelectModal
                open={open}
                options={options}
                onClose={() => {
                    setOpen(false)

                    if (resolver.current) {
                        resolver.current(undefined)
                    }
                }}
                onChange={value => {
                    setOpen(false)

                    if (resolver.current) {
                        resolver.current(value)
                    }
                }}
            />
        </SelectModalContext.Provider>
    )
}
